import {Button, Modal, Space} from "antd";
import {PendingPayout} from "./pending-payout";
import {ReadyToWithdraw} from "./ready-to-withdraw";
import {
  StyledPayoutBalanceSection,
  StyledReadyToWithdrawAndPendingPayoutWrapper,
} from "./styles";
import {useWithdrawalDetails} from "../../../../../../react-query-hooks/payout";
import {usePricingCurrencies} from "@/features/currencies";
import {useState} from "react";
import {PayoutWithdrawalModal} from "./withdrawal-modal";
import {useGetKycStatusContext} from "../../../../../../kyc/useCases/get-kyc-status/context/get-kyc-status";
import {StyledModalContentWrapper} from "../../../../../calls/upcoming-calls/call-card/call-card.style";
import {
  StyledDocUploadVideoWrapper,
  StyledKycStatusRiveWrapper,
} from "../../../../../../kyc/useCases/create-kyc/multi-step/document-upload/file-upload-field/styles";
import {useNavigate} from "react-router-dom";
import {useGetProfileContext} from "../../../../../../context/profile/profile-context";
import {useRive, Layout, Fit, Alignment} from "@rive-app/react-canvas";
import BalanceLocked from "../../../../../../assets/kyc_assets.riv";

export function WithdrawalTabBalanceSection() {
  const navigate = useNavigate();
  const [openWithdrawalModal, setOpenWithdrawalModal] = useState<boolean>(false);
  const [openKycWithdrawalModal, setOpenKycWithdrawalModal] = useState<boolean>(false);
  const pricingCurrencies = usePricingCurrencies();
  const {state} = useGetKycStatusContext();
  const {state: profileState} = useGetProfileContext();
  const [accountBalance, pendingPayout, connectedBank] = useWithdrawalDetails({
    connectBankUniqueIdentifier: "withdrawal_tab",
  });
  const isAnyError = [accountBalance, pendingPayout, connectedBank].some(
    (query) => query.status === "error"
  );
  const isAccountBalanceOrConnectedBankError = [accountBalance, connectedBank].some(
    (query) => query.status === "error"
  );
  const isAccountBalanceOrConnectedBankLoading = [accountBalance, connectedBank].some(
    (query) => query.status === "loading"
  );
  const isAccountBalanceOrConnectedBankFetching = [accountBalance, connectedBank].some(
    (query) => query.isFetching
  );
  const isPendingPayoutOrConnectedBankError = [pendingPayout, connectedBank].some(
    (query) => query.status === "error"
  );
  const isPendingPayoutOrConnectedBankLoading = [pendingPayout, connectedBank].some(
    (query) => query.status === "loading"
  );
  const isPendingPayoutOrConnectedBankFetching = [pendingPayout, connectedBank].some(
    (query) => query.isFetching
  );

  const proceedPastKycCheck = () => {
    setOpenKycWithdrawalModal(false);
    setOpenWithdrawalModal(true);
  };

  const showWithdrawalModal = () => {
    if (profileState.profileData.isKyc_verified) {
      proceedPastKycCheck();
    } else {
      if (state.kycStatus !== "successful") {
        setOpenWithdrawalModal(false);
        setOpenKycWithdrawalModal(true);
      } else {
        proceedPastKycCheck();
      }
    }
  };

  function handleLoginToStripe() {
    pricingCurrencies.handleLoginToStripe();
  }
  const handleModalCancel = () => {
    setOpenWithdrawalModal(false);
  };
  const handleModalOk = () => {
    setOpenWithdrawalModal(false);
  };
  const {RiveComponent} = useRive({
    src: BalanceLocked,
    // animations: ["play"],
    layout: new Layout({
      fit: Fit.Contain, // Change to: rive.Fit.Contain, or Cover
      alignment: Alignment.Center,
    }),
    autoplay: true,
    artboard: "Locked 2",
    stateMachines: "State Machine 1",
  });

  return (
    <StyledPayoutBalanceSection>
      <p>
        Once a withdrawal request is submitted, the money will be sent to your bank
        account
      </p>
      <StyledReadyToWithdrawAndPendingPayoutWrapper>
        <ReadyToWithdraw
          isAccountBalanceOrConnectedBankError={isAccountBalanceOrConnectedBankError}
          isAccountBalanceOrConnectedBankLoading={isAccountBalanceOrConnectedBankLoading}
          isAccountBalanceOrConnectedBankFetching={
            isAccountBalanceOrConnectedBankFetching
          }
          accountBalanceData={accountBalance}
          connectedBankData={connectedBank}
        />
        <PendingPayout
          isPendingPayoutOrConnectedBankError={isPendingPayoutOrConnectedBankError}
          isPendingPayoutOrConnectedBankLoading={isPendingPayoutOrConnectedBankLoading}
          isPendingPayoutOrConnectedBankFetching={isPendingPayoutOrConnectedBankFetching}
          pendingPayoutData={pendingPayout}
          connectedBankData={connectedBank}
        />
        {pricingCurrencies.bankAccount?.isStripeAndActive ? (
          <div>
            <Button size="small" type="primary" onClick={handleLoginToStripe}>
              Withdraw funds
            </Button>
          </div>
        ) : (
          <div>
            <Button
              size="small"
              type="primary"
              disabled={
                isAnyError ||
                (accountBalance.isSuccess && accountBalance.data.balance < 99) ||
                !pricingCurrencies.connectedBankStatus.isBankConnected
              }
              onClick={showWithdrawalModal}
            >
              Withdraw funds
            </Button>
          </div>
        )}
      </StyledReadyToWithdrawAndPendingPayoutWrapper>
      <PayoutWithdrawalModal
        handleModalCancel={handleModalCancel}
        handleModalOk={handleModalOk}
        visibleState={openWithdrawalModal}
        stripeDetails={{
          isStripe: pricingCurrencies.bankAccount?.isStripe ?? false,
          isStripeAndActive: pricingCurrencies.bankAccount?.isStripeAndActive ?? false,
          isStripeAndInactive:
            pricingCurrencies.bankAccount?.isStripeAndInactive ?? false,
        }}
      />
      <Modal
        visible={openKycWithdrawalModal}
        centered
        onCancel={() => setOpenKycWithdrawalModal(false)}
        title="Complete your identity verification"
        footer={null}
      >
        <StyledModalContentWrapper>
          <Space size={24} direction="vertical" style={{display: "flex"}}>
            <Space size={16} direction="vertical" style={{display: "flex"}}>
              <StyledDocUploadVideoWrapper>
                <StyledKycStatusRiveWrapper>
                  <RiveComponent />
                </StyledKycStatusRiveWrapper>
              </StyledDocUploadVideoWrapper>
              <div>
                <p>
                  To withdraw your funds, you’ll need to verify your identity. This helps
                  keep your account secure and ensures compliance with legal requirements
                </p>
              </div>
            </Space>
            <Button
              size="small"
              type="primary"
              block
              onClick={() => {
                navigate("/settings?activeTab=kyc");
              }}
            >
              Complete KYC verification
            </Button>
          </Space>
        </StyledModalContentWrapper>
      </Modal>
    </StyledPayoutBalanceSection>
  );
}
